import { useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { ArrowLeftRight, Baby, CreditCard, Link2, Plus, Sparkles, Users, X } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { isNativeApp } from '@/lib/platform';
import { useParentChildLinks } from '@/lib/parent/child-links';
import { AvatarCircle } from '@/components/avatar-circle';
import { ParentLinkCard } from '@/components/contacts/parent-link-card';
import { useRevokeParentLink } from '@/lib/parent-link';
import { useChildrenAccess } from '@/lib/parent/subscriptions';
import { PLAN_LABELS } from '@/lib/parent/plan-seats';
import { useManagedChildIds } from '@/lib/parent/managed-children';
import { RevisionSheetLinks } from '@/features/skillcards/revision-sheet-links';
import { CreateChildForm } from '@/components/parent/create-child-form';
import { SwitchToChildDialog } from '@/components/parent/switch-account-dialogs';

export function ParentChildrenPage() {
  const { user } = useAuth();
  const parentId = user?.id ?? '';
  // `create` = compte enfant géré (sans email, 0301) ; `link` = lier un compte existant.
  // `?add=create|link` : ouverture directe depuis l'Accueil.
  const search = useSearch({ strict: false }) as { add?: 'create' | 'link' };
  const [panel, setPanel] = useState<'create' | 'link' | null>(search.add ?? null);
  const [adding, setAdding] = useState(!!search.add);
  const [switchTo, setSwitchTo] = useState<{ id: string; name: string } | null>(null);
  const revoke = useRevokeParentLink();
  const managed = useManagedChildIds(parentId);

  const { data, isLoading } = useParentChildLinks(parentId);

  const { data: accessRows } = useChildrenAccess(parentId);
  const accessByChild = new Map((accessRows ?? []).map((a) => [a.child_id, a]));

  const children = data ?? [];
  const hasChildren = children.length > 0;
  // SKILLCARDS à l'unité : utile seulement pour un enfant sans abonnement/pass (web).
  const someoneWithoutAccess = children.some(
    (r) => r.status === 'active' && r.student?.id && !accessByChild.get(r.student.id)?.has_access,
  );

  // Ajouter un enfant : en avant quand il n'y en a aucun, replié sous la liste sinon.
  const addSection =
    hasChildren && !adding ? (
      <button
        type="button"
        onClick={() => setAdding(true)}
        className="border-ink-200 text-ink-700 hover:border-primary-400 mt-4 flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 rounded-2xl border border-dashed font-sans text-sm font-semibold active:scale-[0.98]"
      >
        <Plus className="h-4 w-4" aria-hidden />
        Ajouter un enfant
      </button>
    ) : (
      <div className={hasChildren ? 'mt-4' : 'mb-6'}>
        <div className="mb-4 flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            fullWidth
            variant={panel === 'create' ? 'outline' : 'primary'}
            onClick={() => setPanel((p) => (p === 'create' ? null : 'create'))}
            className="gap-1.5"
          >
            <Baby className="h-4 w-4" />
            {panel === 'create' ? 'Fermer' : 'Créer le compte de mon enfant'}
          </Button>
          <Button
            type="button"
            fullWidth
            variant="outline"
            onClick={() => setPanel((p) => (p === 'link' ? null : 'link'))}
            className="gap-1.5"
          >
            <Link2 className="h-4 w-4" />
            {panel === 'link' ? 'Fermer' : 'Lier un compte existant'}
          </Button>
        </div>
        {panel === 'create' && (
          <CreateChildForm
            parentId={parentId}
            onCreated={(child) => {
              setPanel(null);
              setAdding(false);
              setSwitchTo({ id: child.child_id, name: child.display_name });
            }}
          />
        )}
        {panel === 'link' && <ParentLinkCard userId={parentId} audience="parent" />}
      </div>
    );

  return (
    <div className="pb-safe mx-auto max-w-2xl px-4 py-6 sm:py-8">
      <div className="mb-6">
        <h1 className="font-display text-ink-900 text-2xl font-extrabold">Mes enfants</h1>
        <p className="text-ink-500 mt-1 font-sans text-sm">
          Leurs comptes, leurs accès et les liens avec ton compte.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : !hasChildren ? (
        <>
          <div className="mb-6 flex flex-col items-center gap-3 py-6 text-center">
            <div className="bg-ink-100 flex h-16 w-16 items-center justify-center rounded-full">
              <Users className="text-ink-300 h-7 w-7" />
            </div>
            <p className="font-display text-ink-700 text-base font-bold">Aucun enfant lié</p>
            <p className="text-ink-500 max-w-[280px] font-sans text-sm">
              Crée le compte de ton enfant pour qu’il révise sur ton téléphone, ou lie son compte
              s’il en a déjà un.
            </p>
          </div>
          {addSection}
        </>
      ) : (
        <>
          <ul className="space-y-3">
            {children.map((row) => {
              const id = row.student?.id;
              const name = row.student?.display_name ?? 'ton enfant';
              const acc = id ? accessByChild.get(id) : undefined;
              const isManaged = !!(id && managed.data?.has(id));
              return (
                <li
                  key={row.link_id}
                  className="border-ink-100 ocean:bg-white/5 space-y-3 rounded-2xl border bg-white p-4"
                >
                  <div className="flex items-center gap-3">
                    <AvatarCircle
                      name={row.student?.display_name ?? 'Enfant'}
                      src={row.student?.avatar_url}
                      size="md"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-ink-900 truncate font-sans text-sm font-semibold">
                        {row.student?.display_name ?? '(profil inaccessible)'}
                      </p>
                      {row.status === 'pending' ? (
                        <p className="ocean:text-amber-300 mt-0.5 font-sans text-xs text-amber-600">
                          Invitation en attente : à accepter depuis son compte
                        </p>
                      ) : acc?.has_access ? (
                        <p className="ocean:text-green-300 mt-0.5 font-sans text-xs text-green-700">
                          {acc.source === 'subscription'
                            ? `Accès complet · ${PLAN_LABELS[acc.plan ?? ''] ?? 'abonnement'}`
                            : 'Accès complet (pass)'}
                          {isManaged ? ' · compte sur ton téléphone' : ''}
                        </p>
                      ) : (
                        <p className="text-ink-500 mt-0.5 font-sans text-xs">
                          Version gratuite{isManaged ? ' · compte sur ton téléphone' : ''}
                        </p>
                      )}
                    </div>
                    {id && (
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Retirer ${name} de ton compte ?`)) revoke.mutate(id);
                        }}
                        disabled={revoke.isPending}
                        className="text-ink-400 flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors hover:text-red-500"
                        aria-label={`Retirer ${name}`}
                      >
                        <X className="h-5 w-5" />
                      </button>
                    )}
                  </div>

                  {/* Fiches de révision de l'enfant (0744) : le parent voit — et imprime —
                      ce que son enfant a sous les yeux, cartes non débloquées comprises. */}
                  {row.status === 'active' && id && (
                    <RevisionSheetLinks studentId={id} hint={`vue de ${name}`} />
                  )}

                  {row.status === 'active' && id && (isManaged || !acc?.has_access) && (
                    <div className="flex flex-col gap-2 sm:flex-row">
                      {isManaged && (
                        <Button
                          type="button"
                          size="sm"
                          fullWidth
                          onClick={() => setSwitchTo({ id, name })}
                          className="min-h-[2.75rem] gap-1.5"
                          aria-label={`Passer sur le compte de ${name}`}
                        >
                          <ArrowLeftRight className="h-4 w-4" aria-hidden />
                          Passer sur son compte
                        </Button>
                      )}
                      {!acc?.has_access && (
                        <Link
                          to="/parent/subscriptions"
                          className="border-ink-200 text-ink-800 hover:border-primary-400 flex min-h-[2.75rem] flex-1 items-center justify-center gap-1.5 rounded-xl border px-3 font-sans text-sm font-semibold active:scale-[0.98]"
                        >
                          <CreditCard className="h-4 w-4" aria-hidden />
                          Abonner
                        </Link>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {addSection}

          {/* SKILLCARDS achetées par le parent pour son enfant (migration 0326),
              sous la liste : utile surtout pour un enfant sans abonnement/pass. */}
          {!isNativeApp() && (
            <Link
              to="/parent/skillcards"
              className="border-primary-200 bg-primary-50/60 ocean:bg-white/5 mt-6 flex min-h-[2.75rem] items-center gap-3 rounded-2xl border p-4 active:scale-[0.98]"
            >
              <Sparkles className="text-primary-600 h-5 w-5 shrink-0" aria-hidden />
              <span className="min-w-0">
                <span className="text-ink-900 block text-sm font-semibold">
                  {someoneWithoutAccess
                    ? 'Sans abonnement : acheter des SKILLCARDS'
                    : 'SKILLCARDS : offrir à quelqu’un d’autre'}
                </span>
                <span className="text-ink-600 block text-xs">
                  {someoneWithoutAccess
                    ? 'Le pack du moment ou tout le programme de son année, à l’unité.'
                    : 'Tes enfants ont déjà toutes les cartes avec leur abonnement.'}
                </span>
              </span>
            </Link>
          )}
        </>
      )}

      {switchTo && (
        <SwitchToChildDialog
          childId={switchTo.id}
          childName={switchTo.name}
          onClose={() => setSwitchTo(null)}
        />
      )}
    </div>
  );
}
